/**
 * Floral Fields: Collection Auto-Load & Infinite Scroll
 * Supports automatic scroll loading (infinite scroll) and click-to-load more button.
 * Seamlessly integrates with Dawn facets and Shopify filter updates.
 */

class FFAutoLoad {
  constructor() {
    this.container = null;
    this.productGrid = null;
    this.sentinel = null;
    this.loadMoreBtn = null;
    this.endNotice = null;
    this.observer = null;
    this.mutationObserver = null;
    this.isLoading = false;
    this.nextUrl = null;
    this.paginationType = 'infinite_scroll';

    this.onIntersection = this.onIntersection.bind(this);
    this.onLoadMoreClick = this.onLoadMoreClick.bind(this);
    this.init = this.init.bind(this);

    this.init();
    this.setupMutationObserver();
  }

  init() {
    this.container = document.getElementById('FFAutoLoadContainer');
    this.productGrid = document.getElementById('product-grid');

    if (!this.container || !this.productGrid) {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      return;
    }

    this.paginationType = this.container.dataset.paginationType || 'infinite_scroll';
    this.nextUrl = this.container.dataset.nextUrl || null;
    this.sentinel = document.getElementById('FFInfiniteSentinel');
    this.loadMoreBtn = document.getElementById('FFLoadMoreBtn');
    this.endNotice = document.getElementById('FFAutoLoadEnd');

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (!this.nextUrl) {
      this.showFinished();
      return;
    }

    if (this.paginationType === 'load_more' && this.loadMoreBtn) {
      this.loadMoreBtn.removeEventListener('click', this.onLoadMoreClick);
      this.loadMoreBtn.addEventListener('click', this.onLoadMoreClick);
    } else if (this.paginationType === 'infinite_scroll' && this.sentinel) {
      this.setupIntersectionObserver();
    }
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window) || !this.sentinel) return;

    this.observer = new IntersectionObserver(this.onIntersection, {
      root: null,
      rootMargin: '350px 0px 350px 0px',
      threshold: 0.05
    });

    this.observer.observe(this.sentinel);
  }

  onIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.isLoading && this.nextUrl) {
        this.loadNextPage();
      }
    });
  }

  onLoadMoreClick(e) {
    if (e) e.preventDefault();
    if (!this.isLoading && this.nextUrl) {
      this.loadNextPage();
    }
  }

  setLoadingState(loading) {
    this.isLoading = loading;
    if (this.loadMoreBtn) {
      const textSpan = this.loadMoreBtn.querySelector('.ff-load-more-btn__text');
      const spinner = this.loadMoreBtn.querySelector('.ff-load-more-btn__spinner');
      if (loading) {
        this.loadMoreBtn.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');
        if (textSpan) textSpan.style.opacity = '0.5';
      } else {
        this.loadMoreBtn.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
        if (textSpan) textSpan.style.opacity = '1';
      }
    }

    const infiniteLoader = document.getElementById('FFInfiniteLoader');
    if (infiniteLoader) {
      infiniteLoader.style.opacity = loading ? '1' : '0.4';
    }
  }

  async loadNextPage() {
    if (!this.nextUrl || this.isLoading) return;
    this.setLoadingState(true);

    const urlToFetch = this.nextUrl;

    try {
      const response = await fetch(urlToFetch);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const text = await response.text();

      const parser = new DOMParser();
      const newDoc = parser.parseFromString(text, 'text/html');

      const newGridItems = newDoc.querySelectorAll('#product-grid > .grid__item');
      const currentGrid = document.getElementById('product-grid');

      if (newGridItems.length > 0 && currentGrid) {
        newGridItems.forEach((item) => {
          // Cancel initial scroll trigger animation hiding so items are instantly visible
          item.classList.add('scroll-trigger--cancel');
          item.querySelectorAll('.scroll-trigger').forEach((el) => {
            el.classList.add('scroll-trigger--cancel');
          });
          currentGrid.appendChild(item);
        });

        // Trigger standard window events for quick view / card listeners
        window.dispatchEvent(new CustomEvent('ff:products-loaded', {
          detail: { addedCount: newGridItems.length, url: urlToFetch }
        }));
      }

      // Check for next URL in the fetched HTML
      const nextContainer = newDoc.getElementById('FFAutoLoadContainer');
      const nextPaginationPrev = newDoc.querySelector('.pagination__item--prev');
      let newNextUrl = null;

      if (nextContainer && nextContainer.dataset.nextUrl) {
        newNextUrl = nextContainer.dataset.nextUrl;
      } else if (nextPaginationPrev && nextPaginationPrev.getAttribute('href')) {
        newNextUrl = nextPaginationPrev.getAttribute('href');
      }

      this.nextUrl = newNextUrl;
      if (this.container) {
        this.container.dataset.nextUrl = newNextUrl || '';
      }

      if (newNextUrl) {
        // Update URL quietly in browser history so users can bookmark/share if desired
        try {
          window.history.replaceState({ path: newNextUrl }, '', newNextUrl);
        } catch (_) {}
      } else {
        this.showFinished();
      }
    } catch (err) {
      console.error('[Floral Fields Auto-Load] Error fetching next page:', err);
    } finally {
      this.setLoadingState(false);
    }
  }

  showFinished() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.sentinel) {
      this.sentinel.style.display = 'none';
    }
    if (this.loadMoreBtn) {
      this.loadMoreBtn.style.display = 'none';
    }
    if (this.endNotice) {
      this.endNotice.classList.remove('hidden');
    }
  }

  setupMutationObserver() {
    // When Dawn facets replace #ProductGridContainer via AJAX, re-initialize auto-loader
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer) return;

    this.mutationObserver = new MutationObserver(() => {
      // Re-init with new DOM nodes
      this.init();
    });

    this.mutationObserver.observe(gridContainer, {
      childList: true,
      subtree: false
    });
  }
}

// Instantiate once DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.ffAutoLoadInstance = new FFAutoLoad();
  });
} else {
  window.ffAutoLoadInstance = new FFAutoLoad();
}
